import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ProductJourneyStill, { getProductStill } from '../components/ProductJourneyStill';
import './MobileProductStrip.css';

// Where each phone card looks: the part of the screen worth reading at that
// size (percent of the still), and how far to lean in.
const CROPS = {
  projects: { x: 6, y: 62, zoom: 1.15 },
  idea: { x: 27, y: 38, zoom: 1.25 },
  storyboard: { x: 64, y: 40, zoom: 1.08 },
  video: { x: 62, y: 46, zoom: 1.05 },
};

const ExpandIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M12 3.5h4.5V8M8 16.5H3.5V12M16.5 3.5 11 9M3.5 16.5 9 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
  </svg>
);

// The full screenshot at a readable height, panned sideways by touch,
// opening on the part the card was showing.
function StillViewer({ item, onClose }) {
  const still = getProductStill(item.type);
  const crop = CROPS[item.type] || { x: 50, y: 50 };
  const scrollerRef = useRef(null);
  const closeRef = useRef(null);

  const centerOnCrop = useCallback(() => {
    const scroller = scrollerRef.current;
    const image = scroller && scroller.querySelector('img');
    if (!scroller || !image || !image.clientWidth) return;
    scroller.scrollLeft = Math.max(0, (image.clientWidth * crop.x) / 100 - scroller.clientWidth / 2);
  }, [crop.x]);

  useLayoutEffect(() => {
    const image = scrollerRef.current && scrollerRef.current.querySelector('img');
    if (image && image.complete) centerOnCrop();
  }, [centerOnCrop]);

  useEffect(() => {
    const opener = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, [onClose]);

  return createPortal(
    <div className="still-viewer" role="dialog" aria-modal="true" aria-label={item.title}>
      <div className="still-viewer-bar">
        <div className="still-viewer-heading">
          <span className="still-viewer-eyebrow">{item.eyebrow}</span>
          <h2 className="still-viewer-title">{item.title}</h2>
        </div>
        <button ref={closeRef} type="button" className="still-viewer-close" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </button>
      </div>
      <div className="still-viewer-scroller" ref={scrollerRef}>
        <img src={still.src} alt={still.alt} onLoad={centerOnCrop} />
      </div>
      <p className="still-viewer-hint" aria-hidden="true">Drag to look around</p>
    </div>,
    document.body
  );
}

// The studio walkthrough as a swipeable strip of tall cards. Each card
// frames the legible part of its screen; tapping opens the whole thing.
export default function MobileProductStrip({ items }) {
  const [openIndex, setOpenIndex] = useState(null);
  const close = useCallback(() => setOpenIndex(null), []);

  return (
    <>
      <ol className="product-strip" aria-label="Inside the studio, step by step">
        {items.map((item, index) => (
          <li key={item.type} id={`mobile-product-card-${index + 1}`} className="product-strip-card">
            <button
              type="button"
              className="product-strip-media"
              onClick={() => setOpenIndex(index)}
              aria-label={`View ${item.title} full screen`}
            >
              {item.badge && <span className="mobile-feature-badge">{item.badge}</span>}
              <ProductJourneyStill type={item.type} crop={CROPS[item.type]} loading="lazy" />
              <span className="product-strip-expand" aria-hidden="true">
                <ExpandIcon />
              </span>
            </button>
            <div className="product-strip-copy">
              <span className="mobile-feature-eyebrow">{item.eyebrow}</span>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </div>
          </li>
        ))}
      </ol>
      {openIndex !== null && <StillViewer item={items[openIndex]} onClose={close} />}
    </>
  );
}
